/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Form, FormField, Input, Layout, Modal, Paragraph, Spinner } from '@vwfs-bronson/bronson-react';
import { Formik } from 'formik';
import { t } from 'i18next';
import { uniqBy } from 'lodash';
import { DealerDashboardSubpage } from '../../../resources/base/models/enums';
import {
  getCurrentDealerContacts,
  getDealerContacts,
  getPendingContractById,
  getQuote,
} from '../../../services/api/miles/miles-services';
import { store } from '../../../services/redux';
import { loadingActions, loadingSelectors } from '../../../services/redux/features/loading.slice';
import { DropdownTransfertPopup } from '../fieldset';

interface SalesPerson {
  milesSalesPersonId?: string;
  username?: string;
}

interface ActionPopupProps {
  handleClose: () => void;
  onConfirm: () => void;
}

interface ActionPopupProps {
  handleClose: () => void;
  onConfirm: () => void;
  text: string;
}

export const DashboardActionPopup = ({ handleClose, onConfirm, text }: ActionPopupProps) => {
  return (
    <Layout.Item>
      <Modal
        title={t('app:dashboardActionModalTitle')}
        hideTitle
        center
        buttonConfirmLabel={t('dashboard:buttons:yesButtonTitle')}
        buttonConfirmText={t('dashboard:buttons:yesButtonTitle')}
        buttonCancelLabel={t('dashboard:buttons:noButtonTitle')}
        buttonCancelText={t('dashboard:buttons:noButtonTitle')}
        id="modalId"
        onConfirm={onConfirm}
        onCancel={handleClose}
        onClickOutside={handleClose}
        onClose={handleClose}
        shown
      >
        <Paragraph className="u-mb" dangerouslySetInnerHTML={{ __html: text }} />
      </Modal>
    </Layout.Item>
  );
};

interface DashboardPopupTransfertProps {
  handleClose: () => void;
  onConfirm: (val?) => void;
  salesPersons: any[];
  chooseSalesPerson: (salesPersonId: string) => void;
  quoteIds: string[];
  subpage?: string;
}

export const DashboardPopupTransfer = ({
  handleClose,
  onConfirm,
  salesPersons,
  quoteIds,
  chooseSalesPerson,
  subpage,
}: DashboardPopupTransfertProps) => {
  const formRef: any = useRef();
  const [assignedSalesPeople, setAssignedSalesPeople] = useState<SalesPerson[] | null>(null);
  const [transferToSalesPerson, setTransferToSalesPerson] = useState<SalesPerson[]>([]);
  const isLoading = useSelector(loadingSelectors.selectLoading);

  useEffect(() => {
    if (salesPersons?.length > 0) {
      chooseSalesPerson(salesPersons[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesPersons]);

  const getPlaceholder = (salesPerson: SalesPerson) => {
    return `${salesPerson?.milesSalesPersonId} - ${salesPerson?.username}`;
  };

  const formattedSalesPersons = transferToSalesPerson.map((person) => {
    return {
      label: `${person.milesSalesPersonId} - ${person.username}`,
      value: person.milesSalesPersonId,
    };
  });

  const getAssignedSalesPerson = async (subpage: string = '', id: string) => {
    switch (subpage) {
      case DealerDashboardSubpage.Studies:
      case DealerDashboardSubpage.Summary:
        // eslint-disable-next-line no-case-declarations
        const { dealerData } = await getQuote(id);
        return dealerData?.salesPerson ?? null;
      case DealerDashboardSubpage.Accepted:
        // eslint-disable-next-line no-case-declarations
        const response = await getPendingContractById(id);
        // eslint-disable-next-line no-case-declarations
        const salesPerson = response?.financialProduct?.pendingContracts?.[0]?.dealers?.[0]?.salesPerson;
        return salesPerson ?? null;
      default:
        return null;
    }
  };

  useEffect(() => {
    const fetchQuotes = async (ids: string[]) => {
      if (!ids || ids.length <= 0) {
        return;
      }
      try {
        store.dispatch(loadingActions.setLoading(true));
        const currentDealerContacts = await getCurrentDealerContacts();
        const milesCompanyIds: string[] = [];

        currentDealerContacts?.dealers?.forEach((dealer) => {
          dealer.salesPerson.milesBrokerCompanies?.forEach((company) => {
            if (company?.milesCompanyId) {
              milesCompanyIds.push(company.milesCompanyId);
            }
          });
        });

        const dealerContactsWithMilesCompanyId = await Promise.all(milesCompanyIds.map(getDealerContacts));
        const salesPersonsTransferTo = dealerContactsWithMilesCompanyId
          .map(({ dealers }) => dealers)
          .flat()
          ?.map((dealer) => {
            const { milesSalesPersonId, username } = dealer?.salesPerson ?? {};
            return { milesSalesPersonId, username };
          });

        setTransferToSalesPerson(salesPersonsTransferTo);
        const assigners = (await Promise.all(ids.map((id) => getAssignedSalesPerson(subpage, id)))).filter(
          (el) => !!el
        );
        const filteredAssigns = uniqBy(assigners, 'milesSalesPersonId');
        setAssignedSalesPeople(filteredAssigns);
      } catch {
        /* empty */
      } finally {
        store.dispatch(loadingActions.setLoading(false));
      }
    };
    fetchQuotes(quoteIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteIds]);

  return (
    <Layout.Item className="u-text-center">
      <Modal
        title={t('dashboard:popup:transferToTitle')}
        buttonConfirmLabel={t('dashboard:buttons:transferButtonTitle')}
        buttonConfirmText={t('dashboard:buttons:transferButtonTitle')}
        buttonCancelLabel={t('dashboard:buttons:cancelButtonTitle ')}
        buttonCancelText={t('dashboard:buttons:cancelButtonTitle')}
        id="modalId"
        onConfirm={onConfirm}
        onCancel={handleClose}
        onClickOutside={handleClose}
        onClose={handleClose}
        shown
      >
        <div className="u-mb">
          <Formik initialValues={{ transferToTitle: '' }} onSubmit={() => {}} innerRef={formRef}>
            {() => {
              return (
                <Form>
                  <Layout>
                    <Layout.Item default="1/1">{t('dashboard:popup:transferSelectedDealsTitle')}</Layout.Item>

                    <Layout.Item default="1/1">
                      {assignedSalesPeople?.map((salesPerson, index) => (
                        <FormField
                          key={salesPerson.username || '' + index}
                          inline
                          labelForId={`assigned-salesperson-${index}`}
                          labelText={`${t('dashboard:popup:transferFromTitle')}`}
                          type="input"
                          required
                        >
                          <Layout.Item>
                            <Input
                              className="u-pr-small"
                              id={`assigned-salesperson-${index}`}
                              placeholder={getPlaceholder(salesPerson)}
                              disabled
                            />
                          </Layout.Item>
                        </FormField>
                      ))}
                    </Layout.Item>

                    <Layout.Item default="1/1">
                      <DropdownTransfertPopup
                        name="transferToTitle"
                        label={t('dashboard:popup:transferToTitle')}
                        salesPersons={salesPersons.length > 0 ? salesPersons : formattedSalesPersons}
                        required
                        onChangeFunction={chooseSalesPerson}
                      />
                    </Layout.Item>
                  </Layout>
                </Form>
              );
            }}
          </Formik>
        </div>
        {/* TODO Remove when Bronson fixes BRONSD-1042 */}
        {isLoading && <Spinner fullPage />}
      </Modal>
    </Layout.Item>
  );
};
